import { useEffect, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { ScrollText, ChevronDown, ChevronUp } from 'lucide-react';

const RELATIVE_UNITS = [
  ['year', 31536000], ['month', 2592000], ['week', 604800],
  ['day', 86400], ['hour', 3600], ['minute', 60],
];
function relativeFrom(isoStr) {
  if (!isoStr) return '';
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  for (const [u, sec] of RELATIVE_UNITS) {
    if (diff >= sec) {
      const n = Math.floor(diff / sec);
      return `${n} ${u}${n > 1 ? 's' : ''} ago`;
    }
  }
  return 'just now';
}

/** Owner-only read-only ledger of teammates who have been hard-deleted via
 *  the purge queue. Renders as a collapsible card on /team, below any active
 *  purge queue. Hidden entirely when there is no history. */
export function PurgeHistoryPanel() {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/admin/purge-queue/history').then(r => {
      if (!alive) return;
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    }).catch(e => {
      // 403 is expected for non-owners — silently no-op. Anything else surfaces.
      if (e?.response?.status !== 403) toast.error(parseErr(e));
      if (alive) setRows([]);
    });
    return () => { alive = false; };
  }, []);

  if (!rows || rows.length === 0) return null;

  return (
    <div className="border rounded-lg overflow-hidden" style={{ borderColor: 'hsl(var(--border))' }}
         data-testid="purge-history-panel">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full px-4 py-3 flex items-center gap-3 bg-[hsl(var(--surface-1))] hover:bg-[hsl(var(--surface-2))] transition-colors"
        data-testid="purge-history-toggle"
      >
        <ScrollText size={15} className="text-[hsl(var(--foreground-muted))]" />
        <div className="flex-1 text-left min-w-0">
          <div className="text-[13px] font-semibold">Purge history</div>
          <div className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
            {total} teammate{total === 1 ? '' : 's'} permanently removed from the studio. Owner-only ledger.
          </div>
        </div>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
          {rows.map(r => <HistoryRow key={r.id} row={r} />)}
        </div>
      )}
    </div>
  );
}

function HistoryRow({ row }) {
  const rc = row.reassign_counts || {};
  const bits = [
    rc.creative_lead && `${rc.creative_lead} creative`,
    rc.technical_lead && `${rc.technical_lead} technical`,
    rc.architecture_lead && `${rc.architecture_lead} architecture`,
    rc.milestones && `${rc.milestones} milestones`,
    rc.reports_to && `${rc.reports_to} reports`,
    rc.team_member_stripped && `${rc.team_member_stripped} teams`,
  ].filter(Boolean);

  return (
    <div className="px-4 py-3 grid grid-cols-[1fr_auto] gap-3 items-start" data-testid={`purge-history-row-${row.user_id}`}>
      <div className="min-w-0">
        <div className="text-[13px] font-medium truncate">
          {row.name || row.email}{' '}
          <span className="text-[11.5px] font-normal text-[hsl(var(--foreground-muted))]">
            · {row.role_at_purge || 'no role'}
          </span>
        </div>
        <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] truncate">
          {row.email} · purged by {row.purged_by_email} · {relativeFrom(row.purged_at)}
        </div>
        {bits.length > 0 && (
          <div className="text-[11px] text-[hsl(var(--foreground-subtle))] mt-0.5">
            Reassigned: {bits.join(' · ')}
          </div>
        )}
      </div>
      <div className="text-right">
        <span
          className={`text-[10.5px] px-2 py-0.5 rounded font-semibold ${
            row.purge_mode === 'reassigned'
              ? 'bg-[hsl(var(--success)/.14)] text-[hsl(var(--success))]'
              : 'bg-[hsl(var(--warning)/.14)] text-[hsl(var(--warning))]'
          }`}
        >
          {row.purge_mode === 'reassigned' ? 'Reassigned' : 'Purged anyway'}
        </span>
        {typeof row.days_inactive_at_purge === 'number' && (
          <div className="text-[10.5px] text-[hsl(var(--foreground-subtle))] mt-1">
            {row.days_inactive_at_purge}d inactive at purge
          </div>
        )}
      </div>
    </div>
  );
}
